import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserPlus, UserCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Seller follow toggle — mirrors FollowCategoryButton but posts {seller_id}.
 * Spec: WEB_MOBILE_SPEC_FOLLOW_SELLER.
 * Backend: POST /follows {seller_id} → returns already_following flag.
 * The same DELETE /follows/{id} unfollows.
 */
export default function FollowSellerButton({ sellerId, sellerName }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();

  const followsQ = useQuery({
    queryKey: ["my-follows", user?.id],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/follows");
      return data?.follows || [];
    },
    enabled: !!user,
    staleTime: 60_000,
    retry: 0,
  });

  const existing = useMemo(
    () => (followsQ.data || []).find((f) => f.seller_id === sellerId),
    [followsQ.data, sellerId],
  );

  const followMut = useMutation({
    mutationFn: async () => {
      const { data } = await sharedApi.post("/classifieds/follows", { seller_id: sellerId });
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      toast.success(`You will get an email when ${sellerName || "this seller"} posts a new ad.`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not follow — please try again."),
  });

  const unfollowMut = useMutation({
    mutationFn: async (id) => sharedApi.delete(`/classifieds/follows/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-follows", user?.id] });
      toast.success(`Alerts stopped for ${sellerName || "this seller"}.`);
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not unfollow — please try again."),
  });

  // Don't render the "follow yourself" case
  if (user && user.id === sellerId) return null;

  const statusUnknown = !!user && followsQ.isError;

  const onClick = () => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
      return;
    }
    if (statusUnknown) { followsQ.refetch(); return; }
    if (existing) unfollowMut.mutate(existing.id);
    else followMut.mutate();
  };

  const busy = followMut.isPending || unfollowMut.isPending || (user && followsQ.isLoading);
  const isFollowing = !!existing;

  if (statusUnknown) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={`follow-seller-${sellerId}`}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold bg-white text-slate-400 border border-soft hover:text-[color:var(--brand-blue)]"
      >
        <UserPlus className="w-4 h-4" /> Retry
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid={`follow-seller-${sellerId}`}
      aria-pressed={isFollowing}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-70 ${
        isFollowing
          ? "bg-[color:var(--brand-blue)] text-white hover:bg-[color:var(--brand-blue-hover)]"
          : "bg-white text-slate-700 border border-soft hover:border-[color:var(--brand-blue)] hover:text-[color:var(--brand-blue)]"
      }`}
      title={isFollowing ? "You'll get emails when this seller posts a new ad" : "Follow this seller for new-ad alerts"}
    >
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : isFollowing ? (
        <UserCheck className="w-4 h-4" />
      ) : (
        <UserPlus className="w-4 h-4" />
      )}
      {isFollowing ? "Following" : "Follow"}
    </button>
  );
}
